import { Link } from "@tanstack/react-router";
import type { GameSlug } from "@/data/games";
import { ShareButton } from "@/components/ShareButton";
import { buildShareText } from "@/lib/game-utils";

interface Props {
  score: number;
  total: number;
  best: number;
  bestStreak?: number;
  onRestart: () => void;
  title: string;
  numeral: string;
  hubSlug: GameSlug;
  /** Optional pre-built share text. If omitted, a default is built from the score line. */
  shareText?: string;
  /** Optional emoji grid (used by Quote Wordle). */
  emojiGrid?: string;
}

export function FinishScreen({ score, total, best, bestStreak, onRestart, title, numeral, hubSlug, shareText, emojiGrid }: Props) {
  const pct = Math.round((score / total) * 100);
  const verdict =
    pct === 100 ? "Untouchable." :
    pct >= 80 ? "Bookish brilliance." :
    pct >= 60 ? "Solid scholarship." :
    pct >= 40 ? "Room to read." :
    "Back to the library.";

  const gameUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/games/${hubSlug}`
      : `https://mrbquotes.com/games/${hubSlug}`;

  const finalShare = shareText ?? buildShareText({
    gameName: title,
    scoreLine: `${score} / ${total}${bestStreak !== undefined ? `  ·  best streak ${bestStreak}` : ""}`,
    emojiGrid,
    url: gameUrl,
    extra: "Play free at mrbquotes.com",
  });

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-xl w-full text-center animate-reveal">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Game {numeral} · Results</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">{title}</h1>
        <div className="my-10">
          <div className="font-display text-7xl sm:text-9xl text-gold">
            {score}<span className="text-foreground/30">/{total}</span>
          </div>
          <div className="mt-3 font-display text-2xl">{verdict}</div>
          <div className="mt-3 flex justify-center gap-6 text-sm text-muted-foreground">
            <div>Best: <span className="text-gold font-mono">{best}</span></div>
            {bestStreak !== undefined && <div>Longest streak: <span className="text-gold font-mono">{bestStreak}</span></div>}
          </div>
          {emojiGrid && (
            <pre className="mt-6 inline-block text-2xl leading-tight font-mono whitespace-pre">{emojiGrid}</pre>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <button onClick={onRestart} className="rounded-full bg-gradient-gold px-6 py-3 text-sm font-semibold text-ink">Play again</button>
          <ShareButton text={finalShare} title={`${title} — Mr. B Quotes`} />
          <Link to="/games/$slug" params={{ slug: hubSlug }} className="rounded-full border border-border px-6 py-3 text-sm">Back to hub</Link>
          <Link to="/games" className="rounded-full border border-border px-6 py-3 text-sm">All games</Link>
        </div>
      </div>
    </div>
  );
}
